import { Fragment, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './style.css';
import handShake from '../../assets/hand-shake.png';
import DownloadPanel from '../../components/DownloadPanel/DownloadPanel';
import SplitHero from '../../components/SplitHero/SplitHero';

// Facts on this page come from the Privacy/Terms pages (TFG as operator), the
// tfg-web site (mission wording, partner logos) and the commuter app. Don't
// add numbers, dates or names here unless they're confirmed.

function scrollToWhatWeDo() {
  const element = document.getElementById('what-we-do');
  if (element) element.scrollIntoView({ behavior: 'smooth' });
}

const services = [
  {
    title: "Tap n' Go card",
    description:
      'One physical or virtual card for transport fares. Top up with MoMo, a bank card, or cash at a merchant.',
  },
  {
    title: 'Intercity buses',
    description:
      'Book seats with operators like VIP, STC and Metro Mass, in the app or online.',
  },
  {
    title: 'Trotro, Aayalolo & EV',
    description:
      'Tap your card as you board, and see bus routes and live positions in the app.',
  },
  {
    title: 'Taxi & private drop',
    description:
      'Request a ride, choose a price that works for you, and pay with cash, MoMo or your card.',
  },
  {
    title: 'Merchants & fleets',
    description:
      "Merchants sell Tap n' Go credit in their communities, and fleet managers run their vehicles and drivers on the platform.",
  },
  {
    title: 'USSD access',
    description: 'No smartphone? Key services are available by dialling a USSD code.',
  },
];

const audiences = [
  {
    title: 'Riders',
    description: 'Cashless, convenient fares across every kind of ride.',
  },
  {
    title: 'Drivers & fleet managers',
    description: 'Instant digital payments and simple tools to manage vehicles.',
  },
  {
    title: 'Merchants',
    description: "Earn by selling Tap n' Go credit and top-ups.",
  },
  {
    title: 'Regulators & operators',
    description:
      'One transparent system for the people who plan, regulate and run public transport.',
  },
];

const partners = [
  'VIP',
  'STC',
  'Metro Mass Transit',
  'Aayalolo',
  'GPRTU',
  'National Road Safety Authority',
  'GAPTE',
  'MTN',
  'CalBank',
  'University of Ghana',
];

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Fragment>
      <SplitHero
        brand
        pattern
        brandTitle="Building the future of transport in Ghana."
        brandText={
          <>
            We're Transport for Ghana, the Accra-based team behind Tap n' Go —
            on a mission to transform how Ghana moves, and beyond.
            <span className="block mt-3">
              From the trotro at your corner to the intercity bus across the
              country, we're replacing cash and guesswork with one simple,
              transparent system that works for riders, drivers, operators and
              regulators alike.
            </span>
            <span className="block mt-3">
              Every tap is a step towards safer, fairer and more reliable
              transport for everyone.
            </span>
          </>
        }
        title="About Tap n' Go"
        subtitle="Efficient, reliable and sustainable transport for Ghana — with one card and one app for every journey."
      >
        <p className="mt-6 text-base text-subtext">
          Tap n' Go is operated by Transport for Ghana (TFG), based in Accra.
          We're on a mission to transform how Ghana moves — providing
          efficient, reliable and sustainable transport solutions that
          contribute to the development and prosperity of our nation.
        </p>
        <p className="mt-4 text-base text-subtext">
          Tap n' Go brings that mission to your everyday journey: a single,
          transparent platform that serves riders, drivers, operators and
          regulators alike, so paying for transport is as simple as a tap.
        </p>

        <button
          type="button"
          onClick={scrollToWhatWeDo}
          className="group mt-10 inline-flex items-center gap-3 text-base font-semibold text-go"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-full border border-green-700 animate-bounce group-hover:bg-go group-hover:text-white">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="h-5 w-5"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </span>
          Learn more about us
        </button>
      </SplitHero>

      {/* What we do */}
      <section id="what-we-do">
        <div className="content__wrapper secondary">
          <div style={{ maxWidth: '1184px' }} className="w-full mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-default">What we do</h2>
            <p className="mt-3 text-base text-subtext" style={{ maxWidth: '60ch' }}>
              From a short trotro hop to a cross-country bus trip, Tap n' Go
              covers the ways Ghana travels.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((item) => (
                <div
                  key={item.title}
                  className="bg-white rounded-2xl border border-secondary p-6"
                >
                  <h3 className="text-lg font-semibold text-default">{item.title}</h3>
                  <p className="mt-2 text-base text-subtext">{item.description}</p>
                </div>
              ))}
            </div>
            <Link
              to="/how-it-works"
              className="inline-block mt-8 text-base font-semibold text-go hover:opacity-80"
            >
              See how it works →
            </Link>
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section id="who-its-for">
        <div className="content__wrapper">
          <div className="content row-reverse">
            <h2>Built for everyone in transport</h2>
            <div className="w-full lg:w-auto lg:flex-1 lg:max-w-xl">
              <img
                src={handShake}
                alt="Partners shaking hands"
                className="w-full h-64 sm:h-80 lg:h-96 object-cover object-center rounded-2xl shadow-lg"
              />
            </div>
            <div className="content__txt">
              <h2>Built for everyone in transport</h2>
              <p className="mt-6">
                Tap n' Go believes in the power of collaboration. The platform
                is designed for everyone who keeps Ghana moving.
              </p>
              <ul className="mt-6 space-y-4">
                {audiences.map((item) => (
                  <li key={item.title} className="flex items-start gap-3">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-go" />
                    <span className="text-base text-subtext">
                      <strong className="text-default">{item.title}.</strong>{' '}
                      {item.description}
                    </span>
                  </li>
                ))}
              </ul>
              <Link
                to="/partners"
                className="inline-flex items-center justify-center mt-8 rounded-md bg-green-700 px-6 py-3 text-base font-semibold text-white shadow-sm transition-all duration-200 hover:opacity-90"
              >
                Work with us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section id="partners-list">
        <div className="content__wrapper secondary">
          <div style={{ maxWidth: '1184px' }} className="w-full mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-default">
              Who we work with
            </h2>
            <p className="mt-3 text-base text-subtext" style={{ maxWidth: '60ch' }}>
              Transport operators, industry bodies and payment partners across
              Ghana.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {partners.map((name) => (
                <span
                  key={name}
                  className="rounded-full border border-secondary bg-white px-4 py-2 text-sm font-medium text-default"
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact">
        <div className="content__wrapper">
          <div style={{ maxWidth: '1184px' }} className="w-full mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-default">Get in touch</h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <a
                href="mailto:support@tapngogh.com"
                className="rounded-2xl border border-secondary p-6 hover:border-green-600"
              >
                <p className="text-sm text-subtext">Email</p>
                <p className="mt-1 text-base font-semibold text-default">
                  support@tapngogh.com
                </p>
              </a>
              <a
                href="tel:0308249330"
                className="rounded-2xl border border-secondary p-6 hover:border-green-600"
              >
                <p className="text-sm text-subtext">Customer service</p>
                <p className="mt-1 text-base font-semibold text-default">030 824 9330</p>
              </a>
              <Link
                to="/customer-feedback"
                className="rounded-2xl border border-secondary p-6 hover:border-green-600"
              >
                <p className="text-sm text-subtext">Feedback</p>
                <p className="mt-1 text-base font-semibold text-default">
                  Report an issue →
                </p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <DownloadPanel />
    </Fragment>
  );
}
